import { useRef, useState } from "react";
import { Upload, Loader2, FileUp } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const SAMPLES = {
  flight: "2026-07-15, GAU→DEL 06:00, 4500, 3200, 800, IndiGo, 6E-123, 06:00\n2026-07-16, GAU→DEL 09:30, 4700, 3300, 800, Vistara, UK-745, 09:30",
  train: "2026-07-15, 12345 Rajdhani 3A, 2100\n2026-07-16, 12345 Rajdhani 2A, 3200",
  bus: "2026-07-15, Volvo AC Sleeper, 1200\n2026-07-16, Volvo AC Seater, 900",
};

export default function BulkFareImport({ serviceId, travelType, onImported }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);
  const isFlight = travelType === "flight";
  const cols = isFlight ? "date, label, adult fare, child fare, infant fare, airline, flight no, dep time" : "date, label, price";

  const readFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const r = new FileReader();
    r.onload = () => setText(String(r.result || ""));
    r.readAsText(file);
  };

  const submit = async () => {
    if (!text.trim()) { toast.error("Paste rows or choose a CSV first"); return; }
    setBusy(true); setResult(null);
    try {
      const r = await api.post(`/seller/services/${serviceId}/departures/bulk`, { text });
      setResult(r.data);
      onImported?.(r.data.departures || []);
      if (r.data.added) toast.success(`${r.data.added} fare${r.data.added > 1 ? "s" : ""} imported`);
      if (!r.data.added && (r.data.errors || []).length) toast.error("No valid rows — check the format");
      if (r.data.added) { setText(""); }
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-2" data-testid="bulk-fare-import">
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} data-testid="bulk-fare-open" className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-[11px] font-semibold text-sky-700 hover:border-sky-300">
          <Upload className="h-3 w-3" /> Bulk import fares (CSV)
        </button>
      ) : (
        <div className="rounded-xl border border-sky-100 bg-sky-50/40 p-3" data-testid="bulk-fare-panel">
          <p className="text-[11px] font-semibold text-slate-700">Paste one fare per line, or upload a CSV. Columns:</p>
          <p className="mt-0.5 font-mono text-[10px] text-slate-500">{cols}</p>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} data-testid="bulk-fare-text" placeholder={SAMPLES[travelType] || SAMPLES.train} className="mt-2 w-full rounded-lg border border-slate-200 p-2 font-mono text-[11px] outline-none focus:border-sky-400" />
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <input ref={fileRef} type="file" accept=".csv,text/csv,text/plain" onChange={readFile} className="hidden" data-testid="bulk-fare-file" />
            <button type="button" onClick={() => fileRef.current?.click()} data-testid="bulk-fare-choose" className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-[11px] font-semibold text-slate-600 hover:bg-white"><FileUp className="h-3 w-3" /> Choose CSV</button>
            <button type="button" onClick={submit} disabled={busy} data-testid="bulk-fare-submit" className="inline-flex items-center gap-1 rounded-full bg-sky-600 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-sky-700 disabled:opacity-50">{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />} Import</button>
            <button type="button" onClick={() => { setOpen(false); setResult(null); }} data-testid="bulk-fare-close" className="text-[11px] font-medium text-slate-400 hover:text-slate-600">Close</button>
          </div>
          {result && (
            <div className="mt-2 text-[11px]" data-testid="bulk-fare-result">
              <span className="font-semibold text-emerald-700">{result.added} added.</span>
              {(result.errors || []).length > 0 && <span className="ml-1 text-rose-600">{result.errors.length} skipped: {result.errors.slice(0, 3).map((e) => `line ${e.line} (${e.reason})`).join("; ")}{result.errors.length > 3 ? "…" : ""}</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
